import React, { memo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight } from 'lucide-react';
import { Link } from 'wouter';

interface Announcement {
  id: string;
  text: string;
  link?: string;
  linkText?: string;
}

const STORAGE_KEY = 'dwb_topbar_dismissed';

// Rotate through announcements - using brand colors only
const announcements: Announcement[] = [
  {
    id: 'discount-15',
    text: '🎉 Limited Time: Get 15% OFF your next website project!',
    link: '/enquiry',
    linkText: 'Claim Now',
  },
  {
    id: 'free-consultation',
    text: '💬 Free 30-min consultation for new clients',
    link: '/contact',
    linkText: 'Book Free',
  },
  {
    id: 'new-services',
    text: '✨ New: More Web solutoins Services. Check now!',
    link: '/services',
    linkText: 'Explore',
  },
];

/**
 * Top announcement bar with rotating promos, news, and updates.
 * Features auto-rotation, dismissal with localStorage persistence,
 * and responsive design.
 */
interface TopBarProps {
  onDismiss?: () => void;
}

const TopBar = memo(function TopBar({ onDismiss }: TopBarProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Check if dismissed in current session
  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem(STORAGE_KEY);
      if (dismissed === 'true') {
        setIsVisible(false);
        onDismiss?.();
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, [onDismiss]);

  // Auto-rotate announcements every 8 seconds
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isVisible]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable
    }
  };

  const currentAnnouncement = announcements[currentIndex];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-50 overflow-hidden"
        >
          <div className="bg-primary text-white">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-center py-2 text-sm relative">
                {/* Announcement text */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentAnnouncement.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="flex items-center gap-2 md:gap-3 text-center"
                  >
                    <span className="font-medium text-xs md:text-sm">{currentAnnouncement.text}</span>
                    
                    {currentAnnouncement.link && (
                      <Link href={currentAnnouncement.link}>
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-white/20 hover:bg-white/30 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap">
                          {currentAnnouncement.linkText || 'Learn More'}
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      </Link>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Dots indicator */}
                <div className="hidden md:flex items-center gap-1 ml-4">
                  {announcements.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        idx === currentIndex ? 'bg-white w-3' : 'bg-white/40'
                      }`}
                      aria-label={`View announcement ${idx + 1}`}
                    />
                  ))}
                </div>

                {/* Close button */}
                <button
                  onClick={handleDismiss}
                  className="absolute right-2 p-1 hover:bg-white/20 rounded transition-colors"
                  aria-label="Dismiss announcement"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default TopBar;

// Export height for layout calculations
export const TOPBAR_HEIGHT = 36; // px
